// Built-in application appearance presets. Company branding colors remain
// tenant-controlled; these presets control surfaces, navigation shell and density.

export const BRAND_FONT_FAMILIES = Object.freeze([
  { id: 'inter', label: 'Inter', css: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
  { id: 'poppins', label: 'Poppins', css: 'Poppins, Inter, system-ui, sans-serif' },
  { id: 'manrope', label: 'Manrope', css: 'Manrope, Inter, system-ui, sans-serif' },
  { id: 'dm_sans', label: 'DM Sans', css: '"DM Sans", Inter, system-ui, sans-serif' },
  { id: 'roboto', label: 'Roboto', css: 'Roboto, Arial, sans-serif' },
  { id: 'nunito_sans', label: 'Nunito Sans', css: '"Nunito Sans", Inter, system-ui, sans-serif' },
  { id: 'system', label: 'System Default', css: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif' },
]);

export const resolveBrandFontFamily = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw) return BRAND_FONT_FAMILIES[0].css;
  const preset = BRAND_FONT_FAMILIES.find((font) => font.id === raw || font.css === raw || font.label === raw);
  return preset?.css || raw;
};

export const THEME_PRESETS = Object.freeze({
  legacy_original: {
    label: 'Classic Portal',
    description: 'Current cold-storage workspace with the existing role-based sidebar navigation',
    mode: 'light',
    fontFamily: BRAND_FONT_FAMILIES[0].css,
    radius: 18,
    density: 'comfortable',
    surface: 'legacy',
    shadow: '0 14px 34px rgba(15,23,42,.08)',
    border: '1px solid rgba(148,163,184,.20)',
    pageBackground: 'linear-gradient(155deg,#f4f7ff 0%,#f8fafc 48%,#eefbfd 100%)',
    cardBackground: 'rgba(255,255,255,.90)',
    tableStyle: 'legacy',
    buttonStyle: 'gradient',
    navStyle: 'legacy_sidebar',
    dockStyle: 'launcher',
    motion: 'spring',
    navigationMode: 'legacy_sidebar'
  },
  modern_dashboard: {
    label: 'Modern Dashboard',
    description: 'Responsive light workspace with top module navigation and permission-filtered dropdown submenus',
    mode: 'light',
    fontFamily: BRAND_FONT_FAMILIES[0].css,
    radius: 20,
    density: 'comfortable',
    surface: 'modern_dashboard',
    shadow: '0 12px 32px rgba(15,23,42,.075)',
    border: '1px solid rgba(255,255,255,.94)',
    pageBackground: 'linear-gradient(135deg,#edf5ff 0%,#f9fbff 48%,#fff3f8 100%)',
    cardBackground: 'rgba(255,255,255,.88)',
    tableStyle: 'soft',
    buttonStyle: 'soft_3d',
    navStyle: 'top_dropdown',
    dockStyle: 'none',
    motion: 'soft',
    navigationMode: 'top_dropdown'
  },
  vbweb: {
    label: 'VBWeb',
    description: 'Reference-locked single-window blue-and-white ERP theme with the supplied Gate Pass screen as the master UI across every module',
    mode: 'light',
    fontFamily: BRAND_FONT_FAMILIES[0].css,
    radius: 8,
    density: 'compact',
    surface: 'vbweb',
    shadow: '0 2px 8px rgba(15,50,90,.055)',
    border: '1px solid #d6e3f2',
    pageBackground: '#f5f9fe',
    cardBackground: '#ffffff',
    tableStyle: 'vbweb_grid',
    buttonStyle: 'vbweb_flat',
    navStyle: 'vbweb_topbar',
    dockStyle: 'none',
    motion: 'minimal',
    navigationMode: 'vbweb_topbar'
  },
  basic_erp: {
    label: 'Basic ERP',
    description: 'Reference blue-and-white cold-storage workspace with top module navigation and consistent internal forms',
    mode: 'light',
    fontFamily: BRAND_FONT_FAMILIES[0].css,
    radius: 20,
    density: 'comfortable',
    surface: 'modern_dashboard',
    shadow: '0 12px 32px rgba(15,23,42,.075)',
    border: '1px solid rgba(255,255,255,.94)',
    pageBackground: 'linear-gradient(135deg,#edf5ff 0%,#f9fbff 48%,#fff3f8 100%)',
    cardBackground: 'rgba(255,255,255,.92)',
    tableStyle: 'soft',
    buttonStyle: 'soft_3d',
    navStyle: 'top_dropdown',
    dockStyle: 'none',
    motion: 'soft',
    navigationMode: 'top_dropdown'
  }
});

export const DEFAULT_APPEARANCE_SETTINGS = Object.freeze({
  themeId: 'legacy_original',
  customThemeName: '',
  customThemeDefinition: null,
  previousThemeId: '',
  cardRadius: null,
  shadowIntensity: 1,
  glassIntensity: 1,
  clayIntensity: 0.55,
  density: '',
  fontScale: 1,
  animationIntensity: 1,
});

export const normalizeAppearanceSettings = (value = {}) => {
  const requestedThemeRaw = String(value?.themeId || DEFAULT_APPEARANCE_SETTINGS.themeId);
  // Legacy removed theme IDs migrate directly to the improved Basic ERP workspace
  // so existing companies do not fall back to a different navigation shell.
  const requestedTheme = ['vb_net', 'vb_net_desktop'].includes(requestedThemeRaw)
    ? 'basic_erp'
    : requestedThemeRaw;
  // Light-only workspace: unsupported/old dark theme IDs migrate to Classic Portal.
  const themeId = THEME_PRESETS[requestedTheme] ? requestedTheme : 'legacy_original';
  return {
    ...DEFAULT_APPEARANCE_SETTINGS,
    cardRadius: value?.cardRadius == null ? DEFAULT_APPEARANCE_SETTINGS.cardRadius : Number(value.cardRadius),
    shadowIntensity: Number.isFinite(Number(value?.shadowIntensity)) ? Number(value.shadowIntensity) : DEFAULT_APPEARANCE_SETTINGS.shadowIntensity,
    glassIntensity: Number.isFinite(Number(value?.glassIntensity)) ? Number(value.glassIntensity) : DEFAULT_APPEARANCE_SETTINGS.glassIntensity,
    clayIntensity: Number.isFinite(Number(value?.clayIntensity)) ? Number(value.clayIntensity) : DEFAULT_APPEARANCE_SETTINGS.clayIntensity,
    density: String(value?.density || ''),
    fontScale: Number.isFinite(Number(value?.fontScale)) ? Number(value.fontScale) : DEFAULT_APPEARANCE_SETTINGS.fontScale,
    animationIntensity: Number.isFinite(Number(value?.animationIntensity)) ? Number(value.animationIntensity) : DEFAULT_APPEARANCE_SETTINGS.animationIntensity,
    themeId,
    previousThemeId: String(value?.previousThemeId || ''),
    customThemeName: String(value?.customThemeName || ''),
    customThemeDefinition: value?.customThemeDefinition && typeof value.customThemeDefinition === 'object'
      ? value.customThemeDefinition
      : null,
  };
};

export const resolveThemePreset = (appearance = {}) => {
  const normalized = normalizeAppearanceSettings(appearance);
  const base = THEME_PRESETS[normalized.themeId] || THEME_PRESETS.legacy_original;
  const custom = normalized.customThemeDefinition && typeof normalized.customThemeDefinition === 'object'
    ? normalized.customThemeDefinition
    : null;
  return custom
    ? { ...base, ...custom, label: custom.label || normalized.customThemeName || 'Uploaded Theme' }
    : base;
};
